<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-form class="orangehrm-paper-container">
    <div class="orangehrm-timesheet-header">
      <div class="orangehrm-timesheet-header--title">
        <slot name="header-title"></slot>
      </div>
      <div class="orangehrm-timesheet-header--options">
        <slot name="header-options"></slot>
      </div>
    </div>

    <div v-if="loading" class="orangehrm-timesheet-loader">
      <oxd-loading-spinner />
    </div>
    <div
      v-else-if="!loading && !records"
      class="orangehrm-timesheet-body-message"
    >
      <oxd-alert
        type="warn"
        :show="true"
        :message="$t('time.no_timesheets_found')"
      ></oxd-alert>
    </div>

    <div v-else class="orangehrm-timesheet-body">
      <table :class="tableClasses">
        <thead class="orangehrm-timesheet-table-header">
          <tr class="orangehrm-timesheet-table-header-row">
            <th>名稱</th>
            <th>上班(上半)</th>
            <th>下班(上半)</th>
            <th>上班(下半)</th>
            <th>下班(下半)</th>
          </tr>
        </thead>

        <tbody class="orangehrm-timesheet-table-body">
          <!-- timesheet activities -->
          <tr
            v-for="(record, i) in records"
            :key="record"
            class="orangehrm-timesheet-table-body-row"
          >
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
              }"
            >
              <oxd-input-field
                v-model="record.name"
                type="input"
                required
                @input="limitToFourDigits"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
              }"
            >
              <oxd-input-field
                v-model="record.Astart"
                type="input"
                required
                @input="limitToFourDigits"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
              }"
            >
              <oxd-input-field
                v-model="record.Aend"
                type="input"
                required
                @input="limitToFourDigits"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
              }"
            >
              <oxd-input-field
                v-model="record.Bstart"
                type="input"
                required
                @input="limitToFourDigits"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
              }"
            >
              <oxd-input-field
                v-model="record.Bend"
                type="input"
                required
                @input="limitToFourDigits"
              />
            </td>
            <td class="orangehrm-timesheet-table-body-cell control --flex">
              <oxd-icon-button
                name="trash"
                class="orangehrm-timesheet-icon"
                @click="deleteRow(i)"
                tabindex="-1"
              />
            </td>
          </tr>
          <!-- timesheet activities -->

          <!-- add row -->
          <tr class="orangehrm-timesheet-table-body-row">
            <td class="orangehrm-timesheet-table-body-cell --flex">
              <oxd-icon-button
                name="plus"
                class="orangehrm-timesheet-icon"
                @click="addRow()"
              />
              <oxd-text type="subtitle-2">新增</oxd-text>
            </td>
          </tr>

          <tr
            v-if="records.length === 0"
            class="orangehrm-timesheet-table-body-row"
          >
            <td colspan="9" class="orangehrm-timesheet-table-body-cell">
              {{ $t('general.no_records_found') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="orangehrm-timesheet-footer">
      <div class="orangehrm-timesheet-footer--title">
        <slot name="footer-title"></slot>
      </div>
      <div class="orangehrm-timesheet-footer--options">
        <slot name="footer-options"></slot>
      </div>
    </div>
  </oxd-form>
</template>

<script>
import {validSelection} from '@/core/util/validation/rules';
import {parseDate, parseTimeInSeconds} from '@ohrm/core/util/helper/datefns';
import ActivityDropdown from '@/orangehrmTimePlugin/components/ActivityDropdown.vue';
import ProjectAutocomplete from '@/orangehrmTimePlugin/components/ProjectAutocomplete.vue';
import TimesheetCommentModal from '@/orangehrmTimePlugin/components/TimesheetCommentModal.vue';
import {OxdAlert, OxdSpinner} from '@ohrm/oxd';

export default {
  name: 'Timesheet',

  components: {
    'oxd-alert': OxdAlert,
    'oxd-loading-spinner': OxdSpinner,
  },

  props: {
    records: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timesheetId: {
      type: Number,
      default: null,
    },
  },

  emits: ['update:records'],

  data() {
    return {
      focusedField: null,
    };
  },

  methods: {
    deleteRow(index) {
      const updated = this.records.filter((_, i) => i !== index);
      this.syncRecords(updated);
      this.$nextTick().then(() => {
        if (updated.length === 0) this.addRow();
      });
    },
    addRow() {
      const updated = [
        ...this.records,
        {
          name: '',
          Astart: null,
          Aend: null,
          Bstart: null,
          Bend: null,
        },
      ];
      console.log('updated', updated);
      this.syncRecords(updated);
    },
    updateTime($value, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              date: date,
              duration: $value,
              id: record.dates[date]?.id,
              comment: record.dates[date]?.comment,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateProject($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.project = $value ? $value : null;
          record.customer = $value?._customer ? $value._customer : null;
        }
        return record;
      });
      this.updateActivity(null, index);
      this.syncRecords(updated);
    },
    updateActivity($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.activity = $value ? {id: $value.id, name: $value.label} : null;
        }
        return record;
      });
      this.syncRecords(updated);
    },
    syncRecords(updated) {
      this.$emit('update:records', updated);
    },
    getDuration(entry) {
      // TODO: convert to format from user config
      return entry?.duration ? entry.duration : null;
    },
    onDurationFocus(index, date) {
      this.focusedField = {index, date};
    },
    onDurationBlur() {
      this.focusedField = null;
    },
    limitToFourDigits(event) {
      // 限制为 4 位数字
      if (event.target.value.length > 4) {
        event.target.value = event.target.value.slice(0, 4);
      }
    },
  },
};
</script>

<style src="./timesheet.scss" lang="scss" scoped></style>
<style type="text/css">
.orangehrm-timesheet-table-body-time input {
  box-sizing: border-box;
  color: #64728c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 0.65rem;
  padding: 0.675rem 0.5rem;
  width: 100%;
  min-height: 45px;
  border: 1px solid #e8eaef;
}
</style>
